import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loadMembership } from "../store/billingSlice";
import { isMembershipCurrent } from "../utils/billing";
import { MembershipPanel } from "../components/MembershipPanel";
import DashboardSummaryHeader from "../components/DashboardSummaryHeader";
import DreamTimeline from "../components/DreamTimeline";
import { APP_ROUTES } from "../utils/appRoutes";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchDashboardInsights, fetchDashboardTimeline } from "../store/dashboardSlice";
import { UpgradeBanner, UpgradeModal, UpgradeSuccessBanner } from "../components/upgrade";
import { useIsPremium } from "../hooks/useIsPremium";
import { FREE_PLAN_LIMITS } from "../config/upgrade";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const ownerId = useAppSelector((state) => state.auth.user?.sub);
  const membership = useAppSelector((state) => state.billing.membership);
  const membershipReady = isMembershipCurrent(membership);
  const pollingScope = useRef(0);
  const { insights, insightsLoading, timeline, timelineLoading } = useAppSelector(
    (state) => state.dashboard,
  );
  const { dreamCards } = useAppSelector((state) => state.dashboard);
  const isPremium = useIsPremium();
  const isDowngraded = membershipReady && !isPremium && dreamCards.length > FREE_PLAN_LIMITS.dreams;
  const lockedDreamIds = React.useMemo(() => {
    if (isPremium || dreamCards.length <= FREE_PLAN_LIMITS.dreams) return new Set<string>();
    return new Set(dreamCards.filter((dream) => dream.freeEditable === false).map((dream) => dream.id));
  }, [isPremium, dreamCards]);
  const [showUpgradeGate, setShowUpgradeGate] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<"success" | "cancelled" | "pending" | null>(null);
  const [verifyingUpgrade, setVerifyingUpgrade] = useState(false);

  useEffect(() => {
    dispatch(fetchDashboardInsights());
    dispatch(fetchDashboardTimeline());
  }, [dispatch]);

  useEffect(() => {
    pollingScope.current += 1;
    setVerifyingUpgrade(false);
    setCheckoutStatus(null);
    return () => { pollingScope.current += 1; };
  }, [ownerId]);

  const pollForMembership = useCallback(async () => {
    const scope = pollingScope.current;
    setVerifyingUpgrade(true);
    for (let attempt = 0; attempt < 10; attempt++) {
      if (scope !== pollingScope.current) return;
      const result = await dispatch(loadMembership({ force: true }));
      if (scope !== pollingScope.current) return;
      if (loadMembership.fulfilled.match(result) && isMembershipCurrent(result.payload) && result.payload.premium) {
        setCheckoutStatus("success");
        setVerifyingUpgrade(false);
        return;
      }
      if (attempt < 9) await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    if (scope === pollingScope.current) {
      setVerifyingUpgrade(false);
      setCheckoutStatus("pending");
    }
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    if (!checkout || !ownerId) return;

    params.delete("checkout");
    const newSearch = params.toString();
    navigate({ search: newSearch ? `?${newSearch}` : "" }, { replace: true });

    if (checkout === "success") {
      void pollForMembership();
    } else if (checkout === "cancelled") {
      setCheckoutStatus("cancelled");
    }
  }, [location.search, navigate, ownerId, pollForMembership]);

  const handleCreateDream = () => {
    if (!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams) {
      setShowUpgradeGate(true);
    } else {
      navigate(APP_ROUTES.dreamCreate);
    }
  };

  return (
    <div className="space-y-4 bg-surface-page" data-testid="dashboard-page">
      {(!membershipReady || checkoutStatus === "pending") && <MembershipPanel onUpgrade={() => setShowUpgradeGate(true)} />}
      {verifyingUpgrade && (
        <div
          className="flex items-center gap-3 rounded-2xl px-5 py-4"
          style={{ background: "linear-gradient(135deg, #0088FF 0%, #0066CC 100%)" }}
        >
          <svg className="animate-spin" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="7" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
            <path d="M9 2a7 7 0 0 1 7 7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p className="m-0 text-[14px] font-semibold text-white">
            Verifying your upgrade — this usually takes a few seconds...
          </p>
        </div>
      )}
      {!verifyingUpgrade && checkoutStatus === "success" && (
        <UpgradeSuccessBanner onDismiss={() => setCheckoutStatus(null)} />
      )}
      {checkoutStatus === "cancelled" && (
        <div
          className="flex items-center justify-between rounded-xl px-4 py-3"
          style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
        >
          <p className="m-0 text-[13px]" style={{ color: "#6B7280" }}>
            No new purchase was completed. Your existing membership is unchanged.
          </p>
          <button
            type="button"
            onClick={() => setCheckoutStatus(null)}
            className="ml-3 flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent hover:bg-slate-100"
            aria-label="Dismiss"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}
      {membershipReady && !verifyingUpgrade && checkoutStatus !== "success" && <UpgradeBanner />
      }
      {isDowngraded && !verifyingUpgrade && checkoutStatus !== "success" && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-3"
          style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
            <rect x="2" y="7" width="14" height="9.5" rx="1.5" stroke="#F97316" strokeWidth="1.3"/>
            <path d="M5.5 7V5a3.5 3.5 0 1 1 7 0v2" stroke="#F97316" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <div className="flex-1 min-w-0">
            <p className="m-0 text-[13px] font-semibold" style={{ color: "#9A3412" }}>
              Your Premium plan has ended
            </p>
            <p className="m-0 text-[12px]" style={{ color: "#C2410C" }}>
              Your {dreamCards.length} dreams are safe and visible, but only your first 2 can be edited. Upgrade to unlock everything.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowUpgradeGate(true)}
            className="flex-shrink-0 rounded-lg border-0 px-3 py-1.5 text-[12px] font-bold text-white"
            style={{ background: "#F97316", cursor: "pointer" }}
          >
            Reactivate Premium
          </button>
        </div>
      )}
      <DashboardSummaryHeader
        onCreateDream={handleCreateDream}
        insights={insights}
        loading={insightsLoading}
        hasDreams={timeline.length > 0}
      />
      <DreamTimeline
        timelineData={timeline}
        loading={timelineLoading}
        lockedDreamIds={lockedDreamIds}
        onLockedClick={() => setShowUpgradeGate(true)}
      />
      <UpgradeModal
        open={showUpgradeGate}
        trigger="dream_limit"
        onClose={() => setShowUpgradeGate(false)}
      />
    </div>
  );
};

export default Dashboard;
